import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { WifiOff } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { useDownloadsInfo } from '@/lib/social';
import { useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { HoverTips } from '@/components/ui/hover-tips';
import type { OnboardingStep, SessionInfo } from '@teaplus/shared';
import { API_EVENTS } from '@/lib/api';
import { qk, useActiveProfile, useSession } from '@/lib/queries';
import { BootSplash, RouteLoader, Splash } from '@/components/splash';
import { ErrorState } from '@/components/states';
import { UpdateBanner } from '@/components/update-banner';
import { MandatoryUpdate } from '@/components/mandatory-update';
import { CookieConsent } from '@/components/cookie-consent';
import { RestrictedScreen, Announcements, MaintenanceScreen } from '@/components/platform-notices';
import { NotificationCenter } from '@/components/social/notifications';
import { PlayerHost } from '@/components/player/player-host';
import { PartyFollower } from '@/components/party/party-dock';
import { useReportPresence } from '@/lib/presence';
import { discordBridge, useDiscordPresence } from '@/lib/discord-presence';
import { ProfileAvatar } from '@/components/profile/avatar';

export function FullScreenLoader() {
  return <Splash />;
}

export const STEP_PATH: Record<OnboardingStep, string> = {
  profiles: '/setup/profiles',
  provider: '/setup/provider',
  addons: '/setup/addons',
  preferences: '/setup/preferences',
  complete: '/setup/complete',
  done: '/profiles',
};

export function homeFor(s: SessionInfo | null | undefined) {
  if (!s) return '/welcome';
  if (s.onboardingStep !== 'done') return STEP_PATH[s.onboardingStep];
  return s.activeProfileId ? '/home' : '/profiles';
}

export function Root() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    const onUnauth = () => {
      qc.setQueryData(qk.session, null);
      navigate('/login', { replace: true });
    };
    const onProfile = async () => {
      await qc.invalidateQueries({ queryKey: qk.session });
      // A request sent before a profile was chosen can answer late: stay if one is chosen now.
      if (!qc.getQueryData<SessionInfo | null>(qk.session)?.activeProfileId)
        navigate('/profiles', { replace: true });
    };
    window.addEventListener(API_EVENTS.unauthenticated, onUnauth);
    window.addEventListener(API_EVENTS.profileRequired, onProfile);
    const offOpen = window.teaplus?.onOpen?.((route) => navigate(route));
    return () => {
      window.removeEventListener(API_EVENTS.unauthenticated, onUnauth);
      window.removeEventListener(API_EVENTS.profileRequired, onProfile);
      offOpen?.();
    };
  }, [qc, navigate]);

  return (
    <>
      <div className="desktop-titlebar" aria-hidden />
      <Outlet />
      <RouteLoader />
      <BootSplash />
      <UpdateBanner />
      <MandatoryUpdate />
      <MaintenanceScreen />
      <RestrictedScreen />
      <Announcements />
      <CookieConsent />
      <NotificationCenter />
      <PresenceReporter />
      {discordBridge() && <DiscordPresence />}
      <ScrollRestoration />
      <LiveToaster />
      <HoverTips />
    </>
  );
}

/** Tells friends what you're doing in the app, for their Friends page. */
function PresenceReporter() {
  useReportPresence();
  return null;
}

/** Shows what you're doing on Discord, in the Windows app, when Enable Discord Presence is on. */
function DiscordPresence() {
  const avatar = useRef<HTMLDivElement>(null);
  useDiscordPresence(avatar);
  const profile = useActiveProfile().data?.profile;
  // The avatar as the app draws it, off screen, for the picture Discord shows.
  return profile ? (
    <div ref={avatar} aria-hidden className="pointer-events-none fixed top-0 -left-[9999px]">
      <ProfileAvatar avatar={profile.avatar} image={profile.avatarUrl} size={256} />
    </div>
  ) : null;
}

/**
 * Notifications in the corner, also over a fullscreen player: in fullscreen only the player is
 * drawn, so the toasts move inside it (and back out after).
 */
function LiveToaster() {
  const [host, setHost] = useState<Element | null>(() => document.fullscreenElement);
  useEffect(() => {
    const on = () => setHost(document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  return createPortal(
    <Toaster
      position="bottom-right"
      theme="dark"
      toastOptions={{
        classNames: {
          toast:
            '!bg-elevated !border !border-line !text-text !rounded-2xl !shadow-2xl !shadow-black/50 !font-sans',
          description: '!text-muted',
          success: '[&_[data-icon]]:!text-success',
          error: '[&_[data-icon]]:!text-danger',
        },
      }}
    />,
    host ?? document.body,
  );
}

function useSessionGate() {
  const s = useSession();
  return s;
}

function StartupError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const downloads = useDownloadsInfo().data?.available;
  return (
    <div className="ambient flex min-h-dvh flex-col items-center justify-center gap-2 px-6">
      <ErrorState error={error} onRetry={onRetry} />
      {downloads && (
        <Link to="/offline" className={buttonVariants({ variant: 'ghost' })}>
          <WifiOff className="h-4 w-4" /> Watch your downloads
        </Link>
      )}
    </div>
  );
}

export function RootRedirect() {
  const { data, isPending, isError, refetch, error } = useSessionGate();
  if (isPending) return <FullScreenLoader />;
  if (isError) return <StartupError error={error} onRetry={() => refetch()} />;
  return <Navigate to={homeFor(data)} replace />;
}

export function PublicOnly() {
  const { data, isPending } = useSessionGate();
  if (isPending) return <FullScreenLoader />;
  if (data) return <Navigate to={homeFor(data)} replace />;
  return <Outlet />;
}

export function RequireAuth() {
  const { data, isPending, isError, refetch, error } = useSessionGate();
  const location = useLocation();
  if (isPending) return <FullScreenLoader />;
  if (isError) return <StartupError error={error} onRetry={() => refetch()} />;
  if (!data)
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

const ORDER: OnboardingStep[] = [
  'profiles',
  'provider',
  'addons',
  'preferences',
  'complete',
  'done',
];

export function SetupStepGuard() {
  const { data } = useSession();
  const location = useLocation();
  const requested = location.pathname.split('/')[2] as OnboardingStep;
  const saved = data!.onboardingStep;
  if (saved === 'done') return <Navigate to={homeFor(data)} replace />;
  if (ORDER.indexOf(requested) > ORDER.indexOf(saved))
    return <Navigate to={STEP_PATH[saved]} replace />;
  return <Outlet />;
}

export function RequireOnboarded() {
  const { data } = useSession();
  const location = useLocation();
  const step = data!.onboardingStep;
  // An Install link from the addons site while setup is on its Addons step: install it there.
  if (step === 'addons' && location.pathname === '/addons')
    return <Navigate to={`/setup/addons${location.search}`} replace />;
  if (step !== 'done') return <Navigate to={STEP_PATH[step]} replace />;
  return <Outlet />;
}

export function RequireProfile() {
  const { data } = useSession();
  const location = useLocation();
  if (!data!.activeProfileId)
    return <Navigate to="/profiles" replace state={{ from: location.pathname }} />;
  return (
    <>
      <Outlet />
      <PlayerHost />
      <PartyFollower />
    </>
  );
}
